import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Eye } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import type { DropdownItem } from '@/components/ui/DropdownMenu'
import { Pagination } from '@/components/ui/Pagination'
import { Badge } from '@/components/ui/Badge'
import { StatusDot } from '@/components/ui/StatusDot'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { toast } from '@/components/ui/Toast'
import { useCrudList } from '@/hooks/useCrudList'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { UsersService, type UserItem } from '@/services/modules/UsersService'
import { useAuthStore, type AuthenticatedUser } from '@/stores/authStore'
import { extractApiError } from '@/lib/api-errors'
import { useAuthority } from '@/hooks/useAuthority'
import { cn } from '@/lib/cn'
import { USER_TYPE, USER_TYPE_META, userTypeLabel } from './userTypes'

// Tipos exibidos no group button deste painel (Administrador padrão).
const PANEL_TABS = [
  USER_TYPE.ADMINISTRATOR, USER_TYPE.COLLABORATOR,
  USER_TYPE.DIRECTOR, USER_TYPE.SUPERVISOR, USER_TYPE.AFFILIATE,
  USER_TYPE.SELLER,
]
// Vendedores: apenas consulta (não cadastra/edita por este painel).
const READONLY_TYPES: string[] = [USER_TYPE.SELLER]
// Tipos que possuem painel próprio (impersonação "Visualizar painel").
const VIEWABLE_TYPES: string[] = [USER_TYPE.DIRECTOR, USER_TYPE.SUPERVISOR, USER_TYPE.AFFILIATE, USER_TYPE.SELLER]

export function UsersPage() {
  const nav = useNavigate()
  const startImpersonation = useAuthStore(s => s.startImpersonation)
  const { can } = useAuthority()
  const canCreate = can('administractive.users.create')
  const canEdit = can('administractive.users.edit')
  // A rota de impersonação só tem o gate do grupo (administractive.*|director.*|…).
  const canImpersonate = can('administractive.*')

  async function handleView(u: UserItem) {
    try {
      const data = await UsersService.impersonate(u.id)
      const { accessToken, ...user } = data
      startImpersonation(user as unknown as AuthenticatedUser, accessToken, u.name)
      toast.success({ title: 'Visualizando painel', description: u.name })
      nav('/partner/dashboard')
    } catch (e) {
      toast.error({ title: 'Não foi possível abrir o painel', description: extractApiError(e).message })
    }
  }
  const [type, setType] = useState<string>(USER_TYPE.ADMINISTRATOR)
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar usuário' })

  const filters = useMemo(() => ({ ...sf.filters, type }), [sf.filters, type])
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(UsersService, { size: 10, filters })

  const exp = useDataTableExport({
    filename: 'usuarios',
    service: UsersService as never,
    filters,
    columns: [
      { header: 'Nome', accessor: (u: UserItem) => u.name },
      { header: 'Usuário', accessor: (u: UserItem) => u.username },
      { header: 'Tipo', accessor: (u: UserItem) => userTypeLabel(u.type) },
      { header: 'Hierarquia', accessor: (u: UserItem) => u.hierarchy?.name ?? '' },
      { header: 'Status', accessor: (u: UserItem) => u.status },
    ],
  })

  const columns: DataTableColumn<UserItem>[] = [
    { key: 'name', header: 'Nome', render: u => (
      <div className="flex items-center gap-2.5">
        <Avatar name={u.name} size="sm" />
        <div>
          <div className="font-medium text-gray-800">{u.name}</div>
          <div className="text-[11px] text-gray-500">@{u.username}</div>
        </div>
      </div>
    ), sortable: true },
    { key: 'email', header: 'E-mail', render: u => <span className="text-gray-600">{u.email ?? '—'}</span> },
    { key: 'access', header: 'Acesso / Gestor', render: u => {
      if (u.type === USER_TYPE.ADMINISTRATOR) return <Badge variant="violet">Acesso total</Badge>
      if (u.type === USER_TYPE.COLLABORATOR) return u.hierarchy?.name ? <span className="text-gray-700">{u.hierarchy.name}</span> : <span className="text-gray-400 text-xs">—</span>
      return u.manager?.name ? <span className="text-gray-700">{u.manager.name}</span> : <span className="text-gray-400 text-xs">—</span>
    } },
    { key: 'status', header: 'Status', align: 'center', className: 'w-24', render: u => <StatusDot value={u.status} /> },
  ]

  return (
    <>
      <PageHeader title="Usuários" subtitle="Administradores e colaboradores do sistema"
        crumbs={[{ label: 'Administração' }, { label: 'Usuários' }]}
        actions={READONLY_TYPES.includes(type) || !canCreate ? undefined : <Button onClick={() => nav('/administractive/users/novo')} iconLeft={<Plus size={14} />}>Novo usuário</Button>}
      />

      {/* Group button — tipo de usuário */}
      <div className="mb-3 grid grid-flow-col auto-cols-fr rounded-md ring-1 ring-gray-200 bg-white overflow-hidden max-w-3xl">
        {PANEL_TABS.map((t, i) => {
          const active = type === t
          const meta = USER_TYPE_META[t]
          return (
            <button key={t} type="button" onClick={() => { setType(t); setPage(1) }} aria-pressed={active}
              className={cn(
                'cursor-pointer w-full flex items-center justify-center gap-1.5 px-3 py-2 text-[13px] font-medium transition',
                i > 0 && 'border-l border-gray-200',
                active ? 'bg-brand-500 text-white hover:bg-brand-600' : 'text-gray-600 hover:bg-gray-50',
              )}>
              <meta.icon size={14} /> {meta.label}
            </button>
          )
        })}
      </div>

      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="administractive.users"
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={r => r.id}
        emptyMessage={`Nenhum ${userTypeLabel(type).toLowerCase()} cadastrado.`}
        rowActions={(u) => {
          const actions: DropdownItem[] = []
          if (canImpersonate && VIEWABLE_TYPES.includes(u.type)) {
            actions.push({ key: 'view', label: 'Visualizar painel', icon: <Eye size={14} />, onClick: () => handleView(u) })
          }
          if (canEdit && !READONLY_TYPES.includes(u.type)) {
            actions.push({ key: 'edit', label: 'Editar', icon: <Pencil size={14} />, onClick: () => nav(`/administractive/users/${u.id}/editar`) })
          }
          return actions
        }}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />
      {sf.drawer}
    </>
  )
}
